import { ProjectStatus } from "@/components/ProjectConfiguration";
import { STATUS_CONSTANTS } from "../constants";
import { StatusGroupType, StatusInterface } from "../StatusesContent";
import { KanbanDefaultStates } from "../../ProjectList/CanvasKanban";

export const createDefaultStatusItem = (
  id: string,
  name: string,
  count: number = 0,
): StatusInterface => ({
  id,
  name,
  count,
  color: { value: STATUS_CONSTANTS.DEFAULT_COLOR },
});

export const validateStatusName = (
  statusName: string,
  existingStatuses: ProjectStatus[] = [],
  excludeStatusId?: string,
  defaultStatusNames: string[] = [],
): { isValid: boolean; error?: string } => {
  const trimmedName = statusName.trim();

  if (!trimmedName) {
    return { isValid: false, error: "Status name is required" };
  }

  if (trimmedName.length < STATUS_CONSTANTS.MIN_STATUS_NAME_LENGTH) {
    return {
      isValid: false,
      error: `Status name must be at least ${STATUS_CONSTANTS.MIN_STATUS_NAME_LENGTH} characters long`,
    };
  }

  if (trimmedName.length > STATUS_CONSTANTS.MAX_STATUS_NAME_LENGTH) {
    return {
      isValid: false,
      error: `Status name must be less than ${STATUS_CONSTANTS.MAX_STATUS_NAME_LENGTH} characters`,
    };
  }

  const specialCharRegex = /[<>:"\\|?*]/;
  if (specialCharRegex.test(trimmedName)) {
    return {
      isValid: false,
      error: 'Status name cannot contain special characters: < > : " \\ | ? *',
    };
  }

  if (statusName !== trimmedName || /\s{2,}/.test(statusName)) {
    return {
      isValid: false,
      error:
        "Status name cannot have leading/trailing spaces or multiple consecutive spaces",
    };
  }

  // Simple duplicate check against existing statuses (excluding the one being edited)
  if (existingStatuses.length > 0) {
    const isDuplicateCustom = existingStatuses.some(
      (status) =>
        status.id !== excludeStatusId &&
        status.name.toLowerCase() === trimmedName.toLowerCase(),
    );

    if (isDuplicateCustom) {
      return {
        isValid: false,
        error: "A status with this name already exists",
      };
    }
  }

  // Check for conflicts with default status names
  if (defaultStatusNames.length > 0) {
    // Check if we're editing a default status and trying to rename it back to its original name
    const isEditingDefaultBackToOriginal =
      excludeStatusId &&
      (() => {
        const editing = existingStatuses.find(
          (s) =>
            s.id === excludeStatusId || s.defaultStatusKey === excludeStatusId,
        );
        if (editing?.isDefault && editing.defaultStatusKey) {
          const originalName =
            KanbanDefaultStates[
              editing.defaultStatusKey as keyof typeof KanbanDefaultStates
            ];
          return originalName?.toLowerCase() === trimmedName.toLowerCase();
        }
        return false;
      })();

    const conflictsWithDefault = defaultStatusNames.some(
      (name) => name.toLowerCase() === trimmedName.toLowerCase(),
    );

    if (conflictsWithDefault && !isEditingDefaultBackToOriginal) {
      return {
        isValid: false,
        error: "Cannot use the same name as a default status",
      };
    }
  }

  return { isValid: true };
};

export const sanitizeStatusName = (name: string): string => {
  return name.trim().replace(/\s+/g, " ");
};

export const createNewStatus = (
  name: string,
  type: string,
  isDefault: boolean = false,
): ProjectStatus => ({
  id: crypto.randomUUID(),
  name: sanitizeStatusName(name),
  type,
  isDefault,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const updateStatusName = (
  status: ProjectStatus,
  newName: string,
): ProjectStatus => ({
  ...status,
  name: sanitizeStatusName(newName),
  updatedAt: new Date().toISOString(),
});

export const isStatusInUse = (
  statusId: string,
  statusGroups: StatusGroupType[],
): boolean => {
  return statusGroups.some((group) =>
    group.statuses.some(
      (status: StatusInterface) => status.id === statusId && status.count > 0,
    ),
  );
};

export const getStatusCanvasCount = (
  statusId: string,
  statusGroups: StatusGroupType[],
): number => {
  let totalCount = 0;

  statusGroups.forEach((group) => {
    group.statuses.forEach((status: StatusInterface) => {
      if (status.id === statusId) {
        totalCount += status.count;
      }
    });
  });

  return totalCount;
};

export const hasStatusNameConflict = (
  statusName: string,
  existingStatuses: ProjectStatus[] = [],
  excludeStatusId?: string,
  defaultStatusNames: string[] = [],
): { hasConflict: boolean; conflictingType?: "default" | "custom" } => {
  const trimmedName = statusName.trim().toLowerCase();

  if (defaultStatusNames.length > 0) {
    const isDuplicateDefault = defaultStatusNames.some(
      (defaultName) => defaultName.toLowerCase() === trimmedName,
    );

    if (isDuplicateDefault) {
      return { hasConflict: true, conflictingType: "default" };
    }
  }

  if (existingStatuses.length > 0) {
    const isDuplicate = existingStatuses.some(
      (status) =>
        status.id !== excludeStatusId &&
        status.name.toLowerCase() === trimmedName,
    );

    if (isDuplicate) {
      return { hasConflict: true, conflictingType: "custom" };
    }
  }

  return { hasConflict: false };
};
